import React, { useEffect, useState } from 'react';
import { Button, Col, Form, message, Tooltip } from 'antd';
import { BookyInput, InputType } from '../../Forms/BookyInput';
import { BookySelectDropDown } from '../../Forms/BookySelectDropDown';
import { BookyButton } from '../../Buttons/BookyButton';
import { TextService } from '../../../Services/TextService';
import { IntegrationService } from '../../../Services/IntegrationService';
import { getPreviousCurrentAndNextYearAsOption } from '../../../Helpers/PaymentYearOptions';
import { getPreviousYear } from '../../../Helpers/DateHelpers';
import { WebApiMemberDtoExternalPaymentDto } from '../../../Repository/eventbookyapi';
import { IntegrationType } from '../../../Types/IntegrationType';
import { useAuth } from '../../../Contexts/AuthContext';

interface AddFortnoxInvoiceFormProps {
    memberId: number;
    externalPayments: WebApiMemberDtoExternalPaymentDto[];
    onInvoiceCreated: () => void;
    onCancel: () => void;
}

const AddFortnoxInvoiceForm = ({ memberId, onInvoiceCreated, onCancel, externalPayments }: AddFortnoxInvoiceFormProps) => {
    const [form] = Form.useForm();
    const GetText = TextService.GetText;
    const { user } = useAuth();
    const [isCreating, setIsCreating] = useState(false);
    const [printTemplates, setPrintTemplates] = useState<{ value: string; label: string }[]>([]);
    const selectedYear = Form.useWatch('year', form);

    useEffect(() => {
        IntegrationService.GetFortnoxPrintTemplates()
            .then(templates => setPrintTemplates(templates.map(t => ({ value: t.template!, label: t.name || t.template! }))))
            .catch(() => setPrintTemplates([]));
    }, []);

    const handleCreate = async () => {
        try {
            setIsCreating(true);
            await IntegrationService.CreateFortnoxInvoice({
                memberId: memberId,
                description: form.getFieldValue("description").toString(),
                amount: form.getFieldValue("amount"),
                connectedYear: form.getFieldValue("year").toString(),
                printTemplate: form.getFieldValue("printTemplate") || undefined,
            });
            message.success(GetText("ManagePayments_FortnoxInvoiceCreated"));
            form.resetFields();
            onInvoiceCreated();
        } catch {
            message.warning(GetText("EditMember_ErrorSaving"));
        } finally {
            setIsCreating(false);
        }
    };

    const hasActiveInvoice = externalPayments.some(ep =>
        ep.connectedYear === (selectedYear ?? form.getFieldValue("year"))?.toString() &&
        ep.integrationType === IntegrationType.Fortnox
    );

    return (
        <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0 }}>{GetText("ManagePayments_AddFortnoxInvoiceTitle")}</h4>
                <Button type="link" size="small" onClick={onCancel}>
                    {GetText("AddPaymentLink_CollapseForm")}
                </Button>
            </div>
            <Form form={form} colon={false} labelAlign="left" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}
                initialValues={{ year: getPreviousYear(0) }}
            >
                <Col span={17}>
                    <BookyInput label={GetText("AddPayment_Description")} onClick={handleCreate} name="description" requiredMessage={GetText("AddPaymentLink_DescriptionRequired")} placeholder={GetText("AddPaymentLink_DescriptionPlaceholder")} />
                    <BookyInput type={InputType.number} label={GetText("AddPayment_NoteColumnPlaceHolder")} requiredMessage={GetText("AddPaymentLink_AmountRequired")} onClick={handleCreate} name="amount" req placeholder={GetText("AddPayment_NoteColumnPlaceHolder")} />
                    <BookySelectDropDown label={GetText("AddPayment_YearColumn")} name="year" options={getPreviousCurrentAndNextYearAsOption()} handleChange={() => { }} />
                    {printTemplates.length > 0 && (
                        <BookySelectDropDown
                            label={GetText("ManagePayments_PrintTemplateLabel")}
                            name="printTemplate"
                            allowClear
                            placeholder={GetText("ManagePayments_PrintTemplateDefaultOption")}
                            options={printTemplates}
                            handleChange={() => { }}
                        />
                    )}
                    <Form.Item label={" "}>
                        <Tooltip title={hasActiveInvoice ? GetText("ManagePayments_ActiveFortnoxInvoiceExists") + " " + form.getFieldValue("year")?.toString() : undefined}>
                            <span style={hasActiveInvoice ? { display: 'inline-block', cursor: 'not-allowed' } : undefined}>
                                <BookyButton type="primary" disabled={hasActiveInvoice || user?.hasOnlyReadRights} fullWidth={false} text={GetText("ManagePayments_CreateFortnoxInvoiceButton")} onClick={handleCreate} loading={isCreating} />
                            </span>
                        </Tooltip>

                    </Form.Item>
                </Col>
            </Form>
        </div>
    );
};

export default AddFortnoxInvoiceForm;
